import { z } from 'zod';
/**
 * Signal topic catalog. Each key names a curated topic the external-
 * signal connector pipeline knows how to source. Operators subscribe
 * to a subset during onboarding (or later via `/signals/external`),
 * and the connector pipeline filters its emissions accordingly.
 *
 * The values are short human-readable descriptions surfaced in the
 * topic-picker UI; they're english-only today and keyed so a future
 * i18n layer can attach translations without restructuring the
 * catalog. Adding a topic requires:
 *
 *   1. Extending the literal union below.
 *   2. Adding sample template(s) in
 *      `apps/functions/src/synthesis/external-signals-mock.ts` so the
 *      v0 mock generator can produce a plausible signal for the topic.
 *   3. Optionally referencing the topic in `topicsByIndustry` so it
 *      surfaces as a default suggestion for the matching industry.
 *
 * Phase 5 swaps the mock generator for real news/RSS adapters; the
 * topic keys stay stable so subscription lists migrate without churn.
 */
export const SIGNAL_TOPICS = {
    // Macro / cross-industry
    'tech-layoffs': 'Big-tech layoff announcements',
    'series-a-funding-rounds': 'Series A funding rounds in your space',
    'series-b-funding-rounds': 'Series B funding rounds in your space',
    'm-and-a': 'M&A activity in your sector',
    'fed-rate-changes': 'Fed rate decisions',
    'public-tech-earnings': 'Big-tech earnings calls',
    // Recruiting
    'recruiting-tech-hiring-trends': 'Tech hiring volume + comp benchmarks',
    'recruiting-finance-hiring-trends': 'Finance hiring volume',
    'recruiting-leadership-moves': 'C-suite + VP moves at target companies',
    // SaaS
    'saas-icp-funding': 'Funding rounds at companies in your ICP',
    'saas-competitor-launches': 'Product launches by named competitors',
    'saas-pricing-shifts': 'Pricing announcements from peer SaaS companies',
    // Healthcare
    'healthcare-fda-decisions': 'FDA approvals/rejections in your space',
    'healthcare-cms-rules': 'CMS rule changes affecting your customers',
    // Fintech
    'fintech-regulator-actions': 'CFPB / SEC / OCC actions',
    'fintech-stablecoin-news': 'Stablecoin + crypto regulatory news',
    // Per-region
    'eu-ai-act-updates': 'EU AI Act regulatory updates',
    'us-state-privacy-laws': 'New state privacy laws (CA / TX / etc)',
};
/**
 * Zod schema mirroring `SignalTopicKey`. Use this on schema boundaries
 * (BusinessContext.signalTopics, ExternalSignal.topic) so callers can't
 * write free-text keys that the connector pipeline can't source.
 */
export const SignalTopicKey = z.enum(Object.keys(SIGNAL_TOPICS));
/** Tells you whether a free-text key matches a known topic. */
export function isSignalTopicKey(key) {
    // Use Object.hasOwn so prototype keys (`toString`, `__proto__`, etc.)
    // don't false-positive into the topic catalog.
    return Object.hasOwn(SIGNAL_TOPICS, key);
}
/**
 * Default topic suggestions per industry. Each entry returns 6–10
 * topics the wizard's signal-step pre-selects (operator can deselect
 * before submitting). The mapping is a small heuristic: macro topics
 * apply to everyone; vertical-specific topics layer on top.
 *
 * Industries without a hand-tuned list fall through to the macro-
 * default — a reasonable starting set that's never empty.
 */
export function topicsByIndustry(industry) {
    const macroDefaults = [
        'tech-layoffs',
        'series-a-funding-rounds',
        'series-b-funding-rounds',
        'm-and-a',
        'fed-rate-changes',
        'public-tech-earnings',
    ];
    switch (industry) {
        case 'recruiting':
            return [
                'recruiting-tech-hiring-trends',
                'recruiting-finance-hiring-trends',
                'recruiting-leadership-moves',
                'tech-layoffs',
                'm-and-a',
                'public-tech-earnings',
            ];
        case 'b2b-saas':
        case 'consumer-saas':
        case 'developer-tools':
            return [
                'saas-icp-funding',
                'saas-competitor-launches',
                'saas-pricing-shifts',
                'tech-layoffs',
                'series-a-funding-rounds',
                'series-b-funding-rounds',
                'm-and-a',
            ];
        case 'fintech':
            return [
                'fintech-regulator-actions',
                'fintech-stablecoin-news',
                'fed-rate-changes',
                'tech-layoffs',
                'series-b-funding-rounds',
                'm-and-a',
            ];
        case 'healthtech':
            return [
                'healthcare-fda-decisions',
                'healthcare-cms-rules',
                'series-a-funding-rounds',
                'series-b-funding-rounds',
                'm-and-a',
                'tech-layoffs',
            ];
        case 'agency':
        case 'professional-services':
            return [
                'tech-layoffs',
                'public-tech-earnings',
                'series-b-funding-rounds',
                'm-and-a',
                'fed-rate-changes',
                'recruiting-leadership-moves',
            ];
        case 'ecommerce':
        case 'marketplace':
            return [
                'public-tech-earnings',
                'fed-rate-changes',
                'series-b-funding-rounds',
                'tech-layoffs',
                'm-and-a',
                'us-state-privacy-laws',
            ];
        case 'edtech':
            return [
                'tech-layoffs',
                'series-a-funding-rounds',
                'series-b-funding-rounds',
                'm-and-a',
                'public-tech-earnings',
                'us-state-privacy-laws',
            ];
        case 'media':
            return [
                'tech-layoffs',
                'public-tech-earnings',
                'series-b-funding-rounds',
                'm-and-a',
                'eu-ai-act-updates',
                'us-state-privacy-laws',
            ];
        case 'real-estate':
            return [
                'fed-rate-changes',
                'm-and-a',
                'tech-layoffs',
                'public-tech-earnings',
                'series-b-funding-rounds',
                'us-state-privacy-laws',
            ];
        case 'other':
        default:
            return macroDefaults;
    }
}
